import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Box, BoxProps, chakra } from '@chakra-ui/react';

import { ItemTypes } from './types';
import { ContentComponent } from '../shared/ContentComponent';

export interface ContentBoxProps extends BoxProps {
  id: any;
  left?: number;
  top?: number;
  type: 'text' | 'image' | 'audio';
  src: string;
}

const ContentBox: React.FC<ContentBoxProps> = ({
  id,
  left,
  top,
  type,
  src,
  ...rest
}) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const elementRef = useRef<HTMLDivElement | null>(null);

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ItemTypes.ContentBox,
      item: () => ({
        id,
        left,
        top,
        width,
        height,
        el: elementRef.current,
        type,
        src,
      }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [id, left, top, elementRef.current],
  );

  const attach = useCallback(
    (el) => {
      drag(el);
      elementRef.current = el;
    },
    [drag],
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  useEffect(() => {
    if (elementRef.current) {
      setWidth(elementRef.current.offsetWidth);
      setHeight(elementRef.current.offsetHeight);
    }
  }, [elementRef]);

  return (
    <chakra.div
      opacity={isDragging ? 0 : undefined}
      color="blue.500"
      position={left != null || top != null ? 'absolute' : 'relative'}
      cursor="move"
      left={left}
      top={top}
      ref={attach}
    >
      <ContentBoxPreview type={type} src={src} {...rest} />
    </chakra.div>
  );
};

const ContentBoxPreview: React.FC<Omit<ContentBoxProps, 'id'>> = ({
  type,
  src,
  ...rest
}) => {
  return (
    <Box
      color="blue.500"
      borderRadius="8px"
      borderColor="blue.500"
      backgroundColor="white"
      border="1px"
      fontWeight="bold"
      fontSize="sm"
      flex="1"
      px={type === 'image' ? 2 : 2}
      py={type === 'image' ? 2 : 1}
      w={type === 'image' ? '100px' : 'fit-content'}
      h={type === 'image' ? '100px' : 'fit-content'}
      alignItems={type === 'image' ? 'center' : undefined}
      justifyItems={type === 'image' ? 'center' : undefined}
      transition="all 250ms ease-in-out"
      boxShadow="md"
      {...rest}
    >
      {ContentComponent({ type, src, align: 'justify' })}
    </Box>
  );
};

export { ContentBox, ContentBoxPreview };
export default ContentBox;
